package top.wcpe.allincore.ui.runtime.ir.subscribe

import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import kotlin.jvm.JvmField
import kotlin.jvm.JvmSuppressWildcards

/**
 * 订阅路由器：按 (namespace, topic) 注册 handler 并分发消息。
 *
 * <p>线程模型与有序性：</p>
 * <ul>
 *   <li>{@link #subscribe} 与 {@link #dispatch} 必须线程安全（默认实现见
 *       {@link DefaultSubscribeRouter}）。</li>
 *   <li>{@link #dispatch} 内部直接调用 handler 时**不**保证 handler 在 DSL
 *       执行线程上跑——是否串行化由各 handler 自己的"包装层"决定。
 *       {@code IrInterpreter.buildSubscribeHandler} 与
 *       {@code CallDispatcher.evalNetSubscribe} 都通过
 *       {@code CallDispatcher.wrapSubscribeCallable} 把 handler 提交到
 *       {@code EvalContext.executor}（DslExecutor 是单线程）；同一 IrSession 内
 *       连续 dispatch 因此天然按到达顺序串行。</li>
 *   <li>跨 session 派发顺序无保证：fabric 层 fanout 时各 session 的
 *       SubscribeRouter 实例并发遍历，但每个 session 自身仍保持串行。</li>
 * </ul>
 */
interface SubscribeRouter {

    /**
     * 注册订阅。
     *
     * @param namespace 命名空间
     * @param topic     主题
     * @param handler   收到消息时的回调
     * @return 订阅句柄，可用于取消
     */
    fun subscribe(
        namespace: String,
        topic: String,
        handler: UiEventHandler<@JvmSuppressWildcards Map<String, Any?>>,
    ): Subscription

    /**
     * 向指定 (namespace, topic) 的所有订阅者分发消息。
     *
     * @param namespace 命名空间
     * @param topic     主题
     * @param payload   消息载荷
     */
    fun dispatch(namespace: String, topic: String, payload: Map<String, @JvmSuppressWildcards Any?>)

    /**
     * 静默无操作的常量实现。
     */
    companion object {
        /** 静默无操作的常量实现。 */
        @JvmField
        val NOOP: SubscribeRouter = object : SubscribeRouter {
            override fun subscribe(
                namespace: String,
                topic: String,
                handler: UiEventHandler<@JvmSuppressWildcards Map<String, Any?>>,
            ): Subscription = Subscription.NOOP

            override fun dispatch(namespace: String, topic: String, payload: Map<String, @JvmSuppressWildcards Any?>) {
            }

            override fun toString(): String = "SubscribeRouter.NOOP"
        }
    }
}
